// src/pages/merchant/Vouchers.js — Marchand : valider et suivre les coupons
// Maxula — v1.396.0 : validation groupée des coupons d'une même commande,
// colonne Commande, nom du client invité.
// AbodyStudio Limited — https://abodystudio.com/ — Support@abodystudio.com
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiCheckCircle, FiXCircle, FiSearch, FiRefreshCw, FiTag, FiExternalLink, FiLayers } from 'react-icons/fi';
import { voucherAPI } from '../../services/api';

// Titre du deal cliquable -> fiche produit publique (nouvel onglet), repli en texte si absent.
function DealLink({ deal }) {
  if (!deal) return <>—</>;
  const label = deal.title || '—';
  const ref = deal.slug || deal.id;
  if (!ref) return <>{label}</>;
  return (
    <a href={`/deals/${ref}`} target="_blank" rel="noopener noreferrer"
      className="text-gray-800 hover:text-primary-600 hover:underline inline-flex items-center gap-1"
      title="Voir le produit">
      {label}<FiExternalLink size={12} className="opacity-50 shrink-0" />
    </a>
  );
}

const STATUS = {
  active: { color: 'bg-green-100 text-green-800', text: 'Actif' },
  used: { color: 'bg-gray-200 text-gray-700', text: 'Utilisé' },
  expired: { color: 'bg-red-100 text-red-800', text: 'Expiré' },
  cancelled: { color: 'bg-yellow-100 text-yellow-800', text: 'Annulé' }
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};
const clientName = (u, order) => {
  const n = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '';
  return n || (order && String(order.guestName || '').trim()) || (u && u.email) || '—';
};

const Badge = ({ status }) => {
  const c = STATUS[status] || { color: 'bg-gray-100 text-gray-700', text: status || '—' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>{c.text}</span>;
};

const MerchantVouchers = () => {
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null); // { ok, message, voucher, siblings }
  const [groupBusy, setGroupBusy] = useState(null); // orderId en cours
  const [vouchers, setVouchers] = useState([]);
  const [filter, setFilter] = useState('');
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await voucherAPI.merchantMine({ status: filter || undefined, q: search.trim() || undefined });
      setVouchers(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [load]);

  const validate = async () => {
    const c = code.trim();
    if (!c) return;
    setChecking(true);
    setResult(null);
    try {
      const { data } = await voucherAPI.validate(c);
      setResult({
        ok: true,
        message: data?.message || 'Coupon validé',
        voucher: data?.data,
        siblings: Array.isArray(data?.siblings) ? data.siblings : []
      });
      toast.success('Coupon validé');
      setCode('');
      load();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Coupon invalide';
      setResult({ ok: false, message: msg, voucher: null });
      toast.error(msg);
    } finally {
      setChecking(false);
    }
  };

  // Valide tous les coupons actifs d'une commande (client venu avec plusieurs produits)
  const validateOrder = async (orderId, n) => {
    if (!orderId) return;
    if (!window.confirm(`Valider les ${n} coupon(s) actif(s) de cette commande ? Le client doit être présent.`)) return;
    setGroupBusy(orderId);
    try {
      const { data } = await voucherAPI.validateOrder(orderId);
      toast.success(data?.message || 'Coupons validés');
      setResult((r) => (r && r.voucher && String(r.voucher.orderId) === String(orderId)
        ? { ...r, message: data?.message || r.message, siblings: [] }
        : r));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Validation impossible');
    } finally {
      setGroupBusy(null);
    }
  };

  // Coupons actifs par commande (pour le bouton « Valider la commande »)
  const activeByOrder = {};
  vouchers.forEach((v) => {
    if (v.status === 'active' && v.orderId) activeByOrder[v.orderId] = (activeByOrder[v.orderId] || 0) + 1;
  });
  const firstRowOfOrder = new Set();
  const seenOrders = new Set();
  vouchers.forEach((v) => {
    if (v.status === 'active' && v.orderId && !seenOrders.has(v.orderId)) {
      seenOrders.add(v.orderId); firstRowOfOrder.add(v.id || v.code);
    }
  });

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-1">
        <FiTag className="text-primary-600" size={22} />
        <h1 className="text-2xl font-bold">Coupons / Vouchers</h1>
      </div>
      <p className="text-gray-500 text-sm mb-6">Validez le coupon présenté par le client le jour de la prestation.</p>

      {/* Valider un coupon */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
        <div className="text-sm font-semibold mb-3">Valider un coupon</div>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && validate()}
            placeholder="Ex : MX-AB12CD34"
            className="flex-1 border rounded-lg px-3 py-2 font-mono tracking-wider uppercase"
          />
          <button
            onClick={validate}
            disabled={checking || !code.trim()}
            className="px-5 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <FiSearch size={16} /> {checking ? 'Vérification…' : 'Valider'}
          </button>
        </div>

        {result && (
          <div className={`mt-4 rounded-xl p-4 border ${result.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className={`flex items-center gap-2 font-semibold ${result.ok ? 'text-green-700' : 'text-red-700'}`}>
              {result.ok ? <FiCheckCircle /> : <FiXCircle />} {result.message}
            </div>
            {result.ok && result.voucher && (
              <div className="text-sm text-gray-700 mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                <div><span className="text-gray-500">Code : </span><span className="font-mono font-medium">{result.voucher.code}</span></div>
                <div><span className="text-gray-500">Deal : </span><span className="font-medium"><DealLink deal={result.voucher.deal} /></span></div>
                <div><span className="text-gray-500">Client : </span><span className="font-medium">{clientName(result.voucher.user, result.voucher.order)}</span></div>
                <div><span className="text-gray-500">Validé le : </span><span className="font-medium">{fmtDate(result.voucher.usedAt)}</span></div>
              </div>
            )}
            {result.ok && result.siblings && result.siblings.length > 0 && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <FiLayers /> Cette commande contient encore {result.siblings.length} coupon(s) actif(s)
                </div>
                <ul className="text-sm text-amber-900 mt-1 space-y-0.5">
                  {result.siblings.map((sb) => (
                    <li key={sb.code}><span className="font-mono">{sb.code}</span>{sb.dealTitle ? ` — ${sb.dealTitle}` : ''}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => validateOrder(result.voucher.orderId, result.siblings.length)}
                  disabled={groupBusy === result.voucher.orderId}
                  className="mt-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-60"
                >
                  {groupBusy === result.voucher.orderId ? 'Validation…' : `Valider aussi ces ${result.siblings.length} coupon(s)`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Liste des coupons du marchand */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold">Mes coupons</div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un code…"
                className="border rounded-lg pl-8 pr-2 py-1.5 text-sm w-44"
              />
            </div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm">
              <option value="">Tous</option>
              <option value="active">Actifs</option>
              <option value="used">Utilisés</option>
              <option value="expired">Expirés</option>
            </select>
            <button onClick={load} className="p-2 rounded-lg border hover:bg-gray-50" title="Rafraîchir"><FiRefreshCw size={16} /></button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-8">Chargement…</div>
        ) : vouchers.length === 0 ? (
          <div className="text-center text-gray-400 py-8">Aucun coupon pour le moment.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Commande</th>
                  <th className="py-2 pr-3">Deal</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Statut</th>
                  <th className="py-2 pr-3">Expire</th>
                  <th className="py-2 pr-3">Utilisé</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v) => (
                  <tr key={v.id || v.code} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono font-medium">{v.code}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-gray-500">{v.order?.orderNumber || v.orderNumber || '—'}</td>
                    <td className="py-2 pr-3"><DealLink deal={v.deal} /></td>
                    <td className="py-2 pr-3">{clientName(v.user, v.order)}</td>
                    <td className="py-2 pr-3"><Badge status={v.status} /></td>
                    <td className="py-2 pr-3">{fmtDate(v.expiresAt)}</td>
                    <td className="py-2 pr-3">{v.usedAt ? fmtDate(v.usedAt) : '—'}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {firstRowOfOrder.has(v.id || v.code) && activeByOrder[v.orderId] > 1 && (
                        <button
                          type="button"
                          onClick={() => validateOrder(v.orderId, activeByOrder[v.orderId])}
                          disabled={groupBusy === v.orderId}
                          className="px-3 py-1 rounded-lg bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700 disabled:opacity-60 inline-flex items-center gap-1"
                          title="Valider tous les coupons actifs de cette commande"
                        >
                          <FiLayers size={12} />
                          {groupBusy === v.orderId ? 'Validation…' : `Valider la commande (${activeByOrder[v.orderId]})`}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantVouchers;
